// AuthContext — drzi prihlaseneho uzivatele a vystavuje login/logout/refresh.
//
// Strategie:
// - Pri mountu se zavola GET /api/auth/me. Tichy 401 -> user=null, loading=false.
// - login(email, password) volá POST /api/auth/login a po uspechu nastavi user.
// - logout() vola POST /api/auth/logout a vynuluje user.
// - Posloucha CustomEvent 'auth:unauthorized' z api.ts (kdyz nekde v aplikaci
//   skonci request s 401, automaticky vynuluje user a router presmeruje na /login).
// - Pri navigaci, kdyz URL obsahuje ?error=azure_…, zobrazi toast a vycisti URL.
//   (Pripad: Azure SSO callback presmeroval na / s chybou.)
// - Po Azure SSO redirect-back na "/" provede znovu GET /me, aby objevil novou
//   session cookie (HttpOnly cookie nastavil backend, kontext o tom nevi).
//
// V tomto context-u SCHVALNE NEDELAME navigaci pres useNavigate, protoze metody
// login/logout chceme volat odkudkoli (i mimo Router scope nekde v budoucnu).
// Volajici stranky (LoginPage, Layout) si presmerovani resi samy.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, UNAUTHORIZED_EVENT } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import type { HealthResponse, LoginResponse, MeResponse, User } from '@/types/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** Volá POST /api/auth/login. Vyhodi ApiError pri 401. */
  login: (email: string, password: string) => Promise<User>;
  /** Volá POST /api/auth/logout a vynuluje user. */
  logout: () => Promise<void>;
  /** Manualni reload uzivatele (volá GET /api/auth/me). */
  refresh: () => Promise<void>;
  /** True, pokud má uživatel roli ADMIN. */
  isAdmin: boolean;
  /**
   * True, pokud backend běží v Development prostředí (z GET /api/health → env).
   * Nezávisí na buildu frontendu — je true i v Docker dev stacku, kde je web
   * produkční build. Řídí viditelnost dev-only sekcí (Develop).
   */
  isDev: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Czechove popisky pro `?error=azure_*` z backendu (Azure callback). */
const azureErrorMessages: Record<
  string,
  { title: string; description?: string; variant?: 'destructive' }
> = {
  azure_sso_not_configured: {
    title: 'Office 365 přihlášení zatím není nakonfigurováno',
    description: 'Kontaktujte administrátora.',
  },
  azure_unknown_user: {
    title: 'Účet neexistuje',
    description: 'Váš e-mail není registrovaný. Kontaktujte administrátora.',
    variant: 'destructive',
  },
  azure_inactive_user: {
    title: 'Účet je deaktivován',
    description: 'Kontaktujte administrátora.',
    variant: 'destructive',
  },
  azure_state_mismatch: {
    title: 'Office 365 přihlášení selhalo',
    description: 'Bezpečnostní kontrola se nezdařila, zkuste to prosím znovu.',
    variant: 'destructive',
  },
  azure_oauth_error: {
    title: 'Office 365 přihlášení bylo přerušeno',
    description: 'Microsoft přihlášení neproběhlo. Zkuste to prosím znovu.',
    variant: 'destructive',
  },
  azure_token_failed: {
    title: 'Office 365 přihlášení selhalo',
    description: 'Nepodařilo se ověřit tokeny u Microsoftu.',
    variant: 'destructive',
  },
  azure_missing_code: {
    title: 'Office 365 přihlášení selhalo',
    description: 'Microsoft nevrátil autorizační kód.',
    variant: 'destructive',
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDev, setIsDev] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    try {
      const me = await api<MeResponse>('/auth/me');
      setUser(me?.user ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Nezname chyby ignorujeme (treba sit offline) — proste user=null.
        // Tisk do konzole at je videt v devtools.
        // eslint-disable-next-line no-console
        console.warn('AuthContext.refresh failed:', err);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial probe pri mountu.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Zjisteni prostredi backendu (jednou). /api/health je anonymni a vraci
  // env == EnvironmentName. isDev pak ridi viditelnost dev-only sekci (Develop)
  // nezavisle na buildu frontendu — funguje i v Docker dev stacku.
  useEffect(() => {
    let active = true;
    api<HealthResponse>('/health')
      .then((h) => {
        if (active) setIsDev(h.env === 'Development');
      })
      .catch(() => {
        // Health nedostupne (sit / startup) — necháme isDev = false (sekce skryta).
      });
    return () => {
      active = false;
    };
  }, []);

  // Globalni 401 listener — kdykoli v aplikaci api wrapper zaznamena 401,
  // vynulujeme user. ProtectedRoute pak automaticky presmeruje na /login.
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
  }, []);

  // Detekce Azure SSO callback navratu:
  // a) URL obsahuje ?error=azure_* — zobrazime toast a vyciste URL. Pokud
  //    uzivatel skoncil mimo /login, presmerujeme na /login (chyba == nejsme
  //    prihlasen, tam by nakonec poslal i ProtectedRoute pri prazdnem user-u).
  // b) URL je / (bez query) a user je null — zkusime jeste jednou refresh,
  //    abychom objevili novou session cookie nastavenou backendem.
  const handledErrorRef = useRef<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const azureError = params.get('error');

    if (azureError && azureError.startsWith('azure_')) {
      // Na /login si toast obsluhuje sama LoginPage (ma jeste lepsi kontext);
      // tady resime ostatni routes, kam by nas backend teoreticky mohl pustit.
      if (location.pathname !== '/login') {
        // Zabranime opakovanemu zobrazeni toast-u pro stejny error (StrictMode
        // / double render).
        const key = `${location.pathname}?error=${azureError}`;
        if (handledErrorRef.current !== key) {
          handledErrorRef.current = key;
          const def = azureErrorMessages[azureError] ?? {
            title: 'Office 365 přihlášení selhalo',
            description: 'Neznámá chyba.',
            variant: 'destructive' as const,
          };
          toast(def);
        }

        // Vyciste error param z URL.
        params.delete('error');
        const search = params.toString();
        navigate(
          { pathname: location.pathname, search: search ? `?${search}` : '' },
          { replace: true },
        );
      }
      return;
    }

    // Po uspesnem redirectu z Azure callback-u prijde uzivatel na "/"
    // a my musime znovu zavolat /me, abychom rozpoznali novou cookie.
    // Spousti se jen kdyz jeste nevime o user-ovi a uz jsme dorucili initial probe.
    if (!loading && !user && location.pathname === '/' && !azureError) {
      // Nedrzime extra flag — refresh je idempotentni a levny. Kdyz vrati 401,
      // nic se nestane (user zustane null a ProtectedRoute posle na /login).
      void refresh();
    }
  }, [location.pathname, location.search, loading, user, refresh, navigate, toast]);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const res = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers: { 'Content-Type': 'application/json' },
    });
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } catch {
      // Cookie se na serveru muze nepodarit zneplatnit (treba uz expirovalo) —
      // i tak chceme klienta odhlasit.
    }
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      logout,
      refresh,
      isAdmin: !!user?.roles?.includes('ADMIN'),
      isDev,
    }),
    [user, loading, login, logout, refresh, isDev],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
